import { useAuthStore } from '../../auth/store/useAuthStore';
import type { SyncDispatcherResponse } from './syncTypes';
import { invokeFiscalSideEffectsAfterFlush } from './fiscalSideEffects';
import { syncCatalogSnapshotFromRemote } from '../../catalog/infra/catalogSnapshotSync';
import { pullCustomerDataFromSupabaseApplication } from './pullCustomersUseCase';
import { withSyncMutex } from './syncMutex';
import { markMastersSyncedOk } from './masterSyncPolicy';
import { formatForceFullSyncFailure } from './syncPullErrors';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { recordSyncPullTimestamp, HOSPITALITY_LAST_PULL_AT_KEY } from './syncTimestamps';
import { fetchHospitalitySnapshot } from '../../hospitality/infra/hospitalityRepo';

type SyncPullOutcome = {
  catalogUpdated?: boolean;
  catalogEmpty?: boolean;
  customerUpdated?: boolean;
  hospitalityUpdated?: boolean;
};

export type InvokePendingFiscalSideEffectsOptions = {
  skipFiscal?: boolean;
  reason?: string;
};

/**
 * ADR-017 Fase 4 — la cola offline fue eliminada.
 * Solo invoca side effects fiscales pendientes (facturas / notas de crédito).
 */
export const invokePendingFiscalSideEffects = async (
  options?: InvokePendingFiscalSideEffectsOptions,
): Promise<SyncDispatcherResponse> => {
  if (options?.skipFiscal) {
    return {};
  }
  return invokeFiscalSideEffectsAfterFlush({ force: options?.reason === 'Manual' });
};

export type SyncFullPhase = 'fiscal' | 'catalogPull' | 'fiado';

export type ForceFullSyncOptions = {
  onPhase?: (phase: SyncFullPhase) => void;
};

/**
 * Fuerza una sincronización completa: side effects fiscales + pull catálogo + pull fiado.
 */
export const forceFullSync = async (options?: ForceFullSyncOptions): Promise<SyncPullOutcome> => {
  const failures: string[] = [];
  const onPhase = options?.onPhase;
  let catalogUpdated = false;
  let catalogEmpty: boolean | undefined;
  let customerUpdated = false;

  onPhase?.('fiscal');
  await withSyncMutex(async () => {
    await invokePendingFiscalSideEffects({ reason: 'Manual' }).catch((uploadErr) => {
      console.warn('[forceFullSync] Side effects fiscales fallaron:', uploadErr);
      failures.push('efectos fiscales');
    });
  });

  const { session } = useAuthStore.getState();
  if (session) {
    onPhase?.('catalogPull');
    try {
      const catalogResult = await syncCatalogSnapshotFromRemote({
        hasSession: () => !!useAuthStore.getState().session,
        force: true,
        forceFull: true,
      });
      catalogUpdated = catalogResult.applied;
      catalogEmpty = catalogResult.catalogEmpty;
      markMastersSyncedOk();
    } catch (catalogErr) {
      console.warn('[forceFullSync] Pull catálogo falló:', catalogErr);
      failures.push('catálogo e inventario');
    }

    onPhase?.('fiado');
    try {
      customerUpdated = await pullCustomerDataFromSupabaseApplication({ force: true });
    } catch (customerErr) {
      console.warn('[forceFullSync] Pull customer falló:', customerErr);
      failures.push('clientes');
    }
  }

  if (failures.length > 0) {
    throw new Error(formatForceFullSyncFailure(failures));
  }

  return { catalogUpdated, catalogEmpty, customerUpdated };
};

export const forcePullCatalog = async (): Promise<SyncPullOutcome> => {
  const catalogResult = await syncCatalogSnapshotFromRemote({
    hasSession: () => !!useAuthStore.getState().session,
    force: true,
    forceFull: true,
  });
  return { catalogUpdated: catalogResult.applied, catalogEmpty: catalogResult.catalogEmpty };
};

export const forcePullCustomer = async (): Promise<SyncPullOutcome> => {
  const customerUpdated = await pullCustomerDataFromSupabaseApplication({ force: true });
  return { customerUpdated };
};

export const forcePullHospitality = async (): Promise<SyncPullOutcome> => {
  const branchId = useSessionStore.getState().currentBranchId;
  if (!branchId) {
    return { hospitalityUpdated: false };
  }
  await fetchHospitalitySnapshot(branchId);
  recordSyncPullTimestamp(HOSPITALITY_LAST_PULL_AT_KEY);
  return { hospitalityUpdated: true };
};
